// Shared number formatting helpers for BDE.
//
// Background: several order fields (print_run, page_count, …) are stored as
// VARCHAR on purpose because planners enter values like:
//   "50x"       – "about 50, as discussed"
//   "50,000"    – German thousand separator
//   "50.000"    – German thousand separator (dot)
//   "50000"     – plain integer
//   "ca. 1000"  – free text prefix
// Feeding those through Intl.NumberFormat('de-DE').format(value) yields
// "NaN" for anything that is not a clean Number, which is what we want to
// avoid in every list / detail view.

const DE_FORMATTER = new Intl.NumberFormat('de-DE')

/**
 * Try to coerce a mixed string/number to a Number. Accepts German
 * thousand separators ("50.000" → 50000) and English ones ("50,000" → 50000).
 * Returns `null` when the value contains non-numeric tokens (e.g. "50x").
 */
export function parseLooseNumber(value) {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value

  const raw = String(value).trim()
  if (!raw) return null

  // Strip German/English thousand separators. Keep minus and decimal point.
  // "50.000" or "50,000" → "50000". "1.234,56" → "1234.56".
  const cleaned = raw
    .replace(/\s+/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')       // drop dot thousand-separators
    .replace(/,(?=\d{3}(\D|$))/g, '')        // drop comma thousand-separators
    .replace(',', '.')                       // decimal comma → dot

  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

/**
 * Format for display:
 *   number / clean numeric string  → de-DE grouped output
 *   non-numeric ("50x", "ca. 1000") → returned unchanged so the planner's
 *                                     hint is preserved
 *   null/undefined/empty           → "" (caller decides on placeholder)
 */
export function formatNumberSmart(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = parseLooseNumber(value)
  if (n === null) return String(value)
  return DE_FORMATTER.format(n)
}

/**
 * Same as formatNumberSmart but returns the given `fallback` for empty
 * values (useful in tables where we want a dash).
 */
export function formatNumberOr(value, fallback = '-') {
  const out = formatNumberSmart(value)
  return out === '' ? fallback : out
}
